import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { debounce } from "@mui/material";
import { es } from "date-fns/locale";
import { format } from "date-fns";

import {
	HeaderState,
	transition,
	variantsDate,
	variantsDateLabel,
	variantsDateLabelCollapse,
} from "./HeaderUtils";
import { capitalizeFirst } from "../../utils/capitalize";
import { isNurseAdmin, Schedule } from "../../store/interfaces";
import { AppState } from "../../store/reducer";
import { getHeaders, URI_API } from "../../utils/config";

export default function HeaderDate() {
	const [centerRight, setCenterRight] = useState<string>("0px");
	const [schedulesByHospital, setSchedulesByHospital] = useState([]);
	const [currentSchedule, setCurrentSchedule] = useState<Schedule | undefined>(undefined);
	const today = useMemo(() => new Date(), []);

	const userHospitalId: string | undefined = useSelector(
		(state: AppState) => state.loggedUser,
	).hospital_id;

	useEffect(() => {
		fetch(`${URI_API}/work_schedules/${userHospitalId}`, getHeaders())
			.then((res) => res.json())
			.then((data) => {
				setSchedulesByHospital(data);
			});
	}, []);

	useEffect(() => {
		if (schedulesByHospital.length > 0) {
			const current = findCurrentSchedule(schedulesByHospital, new Date());
			setCurrentSchedule(formatSchedule(current));
		}
	}, [schedulesByHospital]);

	function timeStringToMinutes(timeStr: string) {
		const [h, m] = timeStr.split(":").map(Number);
		return h * 60 + m;
	}

	function dateToMinutes(date: Date) {
		return date.getHours() * 60 + date.getMinutes();
	}

	function findCurrentSchedule(schedules: any, currentDate = new Date()) {
		const currentMinutes = dateToMinutes(currentDate);

		return schedules.find(({ entrance_time, departure_time }: any) => {
			const start = timeStringToMinutes(entrance_time);
			const end = timeStringToMinutes(departure_time);

			if (start <= end) {
				const inside = currentMinutes >= start && currentMinutes < end;
				return inside;
			} else {
				const inside = currentMinutes >= start || currentMinutes < end;
				return inside;
			}
		});
	}

	function formatSchedule(schedule: any) {
		if (!schedule) return undefined;

		return {
			id: schedule.id,
			hospital_id: schedule.hospital_id,
			name: schedule.schedule_name,
			entrance: schedule.entrance_time,
			departure: schedule.departure_time,
		};
	}

	const isAdmin: boolean =
		isNurseAdmin(useSelector((state: AppState) => state.loggedUser).position!) || false;

	const userSchedule: Schedule | undefined = useSelector(
		(state: AppState) => state.loggedUser,
	).schedule;

	const scheduleToShow = userSchedule?.name === "No Disponible" ? currentSchedule : userSchedule;

	const state: HeaderState = useSelector((state: AppState) => state.headerState);

	useEffect(() => {
		const updateCenterRight = () => {
			const rightPos = window.innerWidth / 2;
			setCenterRight(`${rightPos}px`);
		};

		const debouncedUpdate = debounce(updateCenterRight, 200);

		updateCenterRight();
		window.addEventListener("resize", debouncedUpdate);
		return () => window.removeEventListener("resize", debouncedUpdate);
	}, []);

	if (state !== HeaderState.Normal) {
		return (
			<motion.div
				className="header__date-container"
				style={
					!isAdmin
						? {
								right: centerRight,
								alignItems: "center",
								x: "50%",
						  }
						: undefined
				}
				transition={transition}
				variants={variantsDate}
				initial="initial"
				animate={"visible"}
				exit="exit"
			>
				<AnimatePresence
					mode="sync"
					initial={false}
				>
					{state === HeaderState.Extended && (
						<motion.p
							className="header__date"
							transition={transition}
							variants={variantsDateLabel}
							initial="initial"
							animate={"visible"}
							exit="exit"
							key={"Label-Date"}
						>
							{capitalizeFirst(
								format(today, "EEEE d 'de' MMMM 'de' yyyy", { locale: es }),
							)}
						</motion.p>
					)}
					{state === HeaderState.Extended && (
						<motion.p
							className="header__date header__date--variant"
							transition={transition}
							variants={variantsDateLabel}
							initial="initial"
							animate={"visible"}
							exit="exit"
							key={"Label-Area"}
						>
							TURNO {scheduleToShow?.name.toUpperCase()} [{scheduleToShow?.entrance} -{" "}
							{scheduleToShow?.departure}]
						</motion.p>
					)}

					{state === HeaderState.Collapse && (
						<motion.p
							className="header__date header__date--variant"
							style={{
								position: "absolute",
								width: "max-content",
								right: isAdmin ? centerRight : undefined,
								x: isAdmin ? "100%" : "0%",
							}}
							transition={transition}
							variants={variantsDateLabelCollapse}
							initial="initial"
							animate={"visible"}
							exit="exit"
							key={"Label-Collapse"}
						>
							{capitalizeFirst(
								format(today, "EEEE d 'de' MMMM 'de' yyyy", { locale: es }),
							)}{" "}
							| {scheduleToShow?.name.toUpperCase()}
						</motion.p>
					)}
				</AnimatePresence>
			</motion.div>
		);
	}

	return null;
}
